import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Edit, MapPin, Users, CheckCircle, Clock, FileText, Share2, Copy, Check, Download } from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../hooks/useAuth';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { QRCodeSVG } from 'qrcode.react';

export default function CampaignDetailPage() {
  const { id } = useParams();
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showSurveyDialog, setShowSurveyDialog] = useState(false);
  const [selectedSurveyId, setSelectedSurveyId] = useState<string>('');

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => api.getCampaign(id || ''),
    enabled: !!id && !!user?.organization_id,
  });

  const { data: responses } = useQuery({
    queryKey: ['campaign-responses', id],
    queryFn: () => api.getCampaignResponses(id || ''),
    enabled: !!id,
  });

  const { data: surveyTemplates } = useQuery({
    queryKey: ['survey-templates', user?.organization_id],
    queryFn: () => api.getSurveyTemplates(user?.organization_id || ''),
    enabled: !!user?.organization_id,
  });

  const surveyTemplate = campaign?.survey_template_id 
    ? surveyTemplates?.find(t => t.id === campaign.survey_template_id)
    : null;

  const updateCampaignMutation = useMutation({
    mutationFn: async (data: { status?: 'draft' | 'active' | 'paused' | 'completed' | 'archived'; metadata?: Record<string, unknown>; survey_template_id?: string }) => {
      if (!id) throw new Error('No campaign ID');
      return api.updateCampaign(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaign', id] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });

  const handleStatusChange = (newStatus: string) => {
    updateCampaignMutation.mutate({ status: newStatus as 'draft' | 'active' | 'paused' | 'completed' | 'archived' });
  };

  const handleInstructionsSave = (instructions: string) => {
    const currentMetadata = (campaign?.metadata as Record<string, unknown>) || {};
    updateCampaignMutation.mutate({
      metadata: { ...currentMetadata, instructions }
    });
  };

  const handleLinkSurvey = () => {
    if (selectedSurveyId) {
      updateCampaignMutation.mutate({ survey_template_id: selectedSurveyId });
      setShowSurveyDialog(false);
      setSelectedSurveyId('');
    }
  };

  const handleCreateNewSurvey = () => {
    window.location.href = '/surveys/new';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Campaign not found</p>
        <Link to="/campaigns" className="text-primary hover:underline mt-2 inline-block">
          Back to campaigns
        </Link>
      </div>
    );
  }

  const responseCount = responses?.length || 0;
  const targetCount = campaign.target_representatives || 0;
  const progress = targetCount > 0 ? Math.round((responseCount / targetCount) * 100) : 0;

  const statusColors: Record<string, string> = {
    draft: 'bg-gray-500',
    active: 'bg-green-500',
    paused: 'bg-yellow-500',
    completed: 'bg-blue-500',
    archived: 'bg-gray-400',
  };

  const shareUrl = `${window.location.origin}/public/${campaign.id}`;

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadQRCode = () => {
    const svg = document.getElementById('campaign-qr-code');
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `${campaign.name.replace(/\s+/g, '-')}-qr-code.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/campaigns"
            className="p-2 hover:bg-muted rounded-md transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{campaign.name}</h1>
              <Badge className={statusColors[campaign.status] || 'bg-gray-500'}>
                {campaign.status}
              </Badge>
            </div>
            <p className="text-muted-foreground">{campaign.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share & QR
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Share Campaign</DialogTitle>
                <DialogDescription>
                  Share "{campaign.name}" with respondents
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Share Link</label>
                  <div className="flex gap-2">
                    <input
                      value={shareUrl}
                      readOnly
                      className="flex-1 px-3 py-2 border rounded-md bg-background"
                    />
                    <Button size="icon" variant="outline" onClick={copyToClipboard}>
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">QR Code</label>
                  <div className="flex justify-center p-4 bg-white border rounded-lg">
                    <QRCodeSVG
                      id="campaign-qr-code"
                      value={shareUrl}
                      size={200}
                      level="M"
                      includeMargin
                    />
                  </div>
                  <Button variant="outline" className="w-full" onClick={downloadQRCode}>
                    <Download className="w-4 h-4 mr-2" />
                    Download QR Code
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Link
            to={`/campaigns/${id}/edit`}
            className="inline-flex items-center px-4 py-2 border rounded-md hover:bg-muted transition-colors"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Link>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="p-6 bg-card rounded-lg border border-border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <CheckCircle className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Responses</p>
              <p className="text-2xl font-bold">{responseCount}</p>
            </div>
          </div>
        </div>
        <div className="p-6 bg-card rounded-lg border border-border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Target</p>
              <p className="text-2xl font-bold">{targetCount}</p>
            </div>
          </div>
        </div>
        <div className="p-6 bg-card rounded-lg border border-border">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Clock className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Progress</p>
              <p className="text-2xl font-bold">{progress}%</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="p-6 bg-card rounded-lg border border-border">
          <h3 className="text-lg font-semibold mb-4">Campaign Settings</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={campaign.status}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">GPS Required</label>
              <Badge variant={campaign.require_gps ? "default" : "secondary"}>
                {campaign.require_gps ? 'Yes' : 'No'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Access Mode</label>
              <Badge variant="outline">{campaign.access_mode || 'authenticated'}</Badge>
            </div>
          </div>
        </div>

        <div className="p-6 bg-card rounded-lg border border-border">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Linked Survey
          </h3>
          {surveyTemplate ? (
            <div className="space-y-2">
              <p className="font-medium">{surveyTemplate.name}</p>
              <p className="text-sm text-muted-foreground">
                {((surveyTemplate.schema?.fields as unknown[]) || []).length || 0} questions
              </p>
              <Link
                to={`/surveys/${surveyTemplate.id}`}
                className="text-sm text-primary hover:underline"
              >
                View Survey →
              </Link>
              <button
                onClick={() => setShowSurveyDialog(true)}
                className="text-sm text-primary hover:underline block mt-2"
              >
                Change Survey →
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-muted-foreground">No survey linked</p>
              <Button onClick={() => setShowSurveyDialog(true)}>
                Add Survey
              </Button>
            </div>
          )}
        </div>

        <Dialog open={showSurveyDialog} onOpenChange={setShowSurveyDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Link Survey</DialogTitle>
              <DialogDescription>
                Select an existing survey or create a new one
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Select Existing Survey</label>
                <Select value={selectedSurveyId} onValueChange={setSelectedSurveyId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a survey..." />
                  </SelectTrigger>
                  <SelectContent>
                    {surveyTemplates?.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={handleCreateNewSurvey}
                  className="flex-1"
                >
                  Create New Survey
                </Button>
                <Button 
                  onClick={handleLinkSurvey}
                  disabled={!selectedSurveyId}
                  className="flex-1"
                >
                  Link Survey
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="p-6 bg-card rounded-lg border border-border">
        <h3 className="text-lg font-semibold mb-4">Survey Instructions</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Add instructions for surveyors on how to conduct this survey
        </p>
        <Textarea
          id="campaign-instructions"
          placeholder="Enter survey instructions, guidelines, or notes for surveyors..."
          defaultValue={(campaign.metadata?.instructions as string) || ''}
          className="min-h-[150px]"
        />
        <Button 
          onClick={() => {
            const instructions = (document.getElementById('campaign-instructions') as HTMLTextAreaElement)?.value;
            if (instructions !== undefined) {
              handleInstructionsSave(instructions);
            }
          }} 
          className="mt-4"
          disabled={updateCampaignMutation.isPending}
        >
          {updateCampaignMutation.isPending ? 'Saving...' : 'Save Instructions'}
        </Button>
      </div>

      {campaign.start_date && (
        <div className="text-sm text-muted-foreground">
          <span className="font-medium">Duration:</span>{' '}
          {new Date(campaign.start_date).toLocaleDateString()} -{' '}
          {campaign.deadline ? new Date(campaign.deadline).toLocaleDateString() : 'No deadline'}
        </div>
      )}

      <div className="flex space-x-4">
        <Link
          to={`/campaigns/${id}/responses`}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
        >
          View Responses
        </Link>
        <Link
          to={`/campaigns/${id}/map`}
          className="inline-flex items-center px-4 py-2 border rounded-md hover:bg-muted transition-colors"
        >
          <MapPin className="w-4 h-4 mr-2" />
          Map View
        </Link>
      </div>
    </div>
  );
}